import React, { useEffect, useState } from 'react';
import { authAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';

/**
 * Email notification preferences -- GitHub issue #76.
 *
 * Self-contained (owns its own fetching and API calls) rather than
 * presentational-with-a-parent-owning-state like DelegationsPanel: there is one
 * caller (ProfilePage) and, unlike delegations and home nodes, no admin-facing
 * counterpart at all. Nobody sets somebody else's notification preferences.
 * BookmarksPanel's shape, for the same reasons.
 *
 * EVERY BOX STARTS UNTICKED, and that is not a rendering default -- it is the
 * honest picture of the data. A missing preference row means email is off, so
 * somebody who never opens this screen is never emailed. The in-app delivery is
 * unaffected either way: an @mention still lands in My Tasks (issue #75).
 *
 * The category list, its LABELS and the `wired` flag all come from the backend
 * rather than a hardcoded array here -- the rule GET /api/roles/resources
 * follows for the permission matrix, which exists because the hardcoded JS copy
 * had already drifted from the enum.
 *
 * A category that nothing produces yet is OFFERED rather than hidden: the
 * choice is saved and takes effect the day that trigger ships, which is what
 * the issue asks for ("also other future options"). It says so on the row, so
 * the offer is not a promise it silently fails to keep -- and it is harmless
 * precisely because the default is off.
 */
function NotificationPreferencesPanel() {
  const [entries, setEntries] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    authAPI.getMyNotificationPreferences()
      .then((res) => {
        setEntries(res.data.preferences || []);
        setEnabled(res.data.enabled);
      })
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (eventType) => {
    setSaved(false);
    setEntries((prev) => prev.map((e) => (
      e.event_type === eventType ? { ...e, email_enabled: !e.email_enabled } : e
    )));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      entries.forEach((e) => { payload[e.event_type] = e.email_enabled; });
      const res = await authAPI.setMyNotificationPreferences({ preferences: payload });
      setEntries(res.data.preferences || []);
      setSaved(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading notification settings...</div>;

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}

      {/* The DEPLOYMENT flag, not the user's. Both must be true for mail to go
          out, and a ticked box while the environment has it switched off would
          otherwise be a silent lie. */}
      {!enabled && (
        <div className="alert alert-warning">
          Email notifications are switched off for this environment, so nothing
          will be sent yet. Your choices here are saved and take effect as soon
          as it is enabled.
        </div>
      )}

      <div className="form-group">
        {entries.map((entry) => (
          <label key={entry.event_type} className="notification-pref-row">
            <input
              type="checkbox"
              checked={entry.email_enabled}
              onChange={() => toggle(entry.event_type)}
            />
            <span>
              <span className="notification-pref-label">{entry.label}</span>
              {!entry.wired && (
                <span className="notification-pref-note">
                  Not sending yet — your choice is saved and takes effect when
                  this becomes available.
                </span>
              )}
            </span>
          </label>
        ))}
      </div>

      <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save notification settings'}
      </button>
      {saved && (
        <span style={{ marginLeft: '10px', color: 'var(--success-ink)', fontSize: '13px' }}>
          ✅ Saved
        </span>
      )}
    </div>
  );
}

export default NotificationPreferencesPanel;
